import { useEffect, useState } from 'react';
import { adminApi, ApiError, type AccountRequest } from '../lib/api.js';
import { useEscape } from '../lib/useEscape.js';

interface Props {
  onClose: () => void;
  /** Prévient le tableau de bord que la pastille doit changer. */
  onChange: (pending: number) => void;
}

/** « il y a 3 j », depuis une date ISO renvoyée par l'API. */
function since(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "aujourd'hui";
  if (days === 1) return 'hier';
  return `il y a ${days} j`;
}

export function AdminPanel({ onClose, onChange }: Props) {
  const [requests, setRequests] = useState<AccountRequest[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEscape(onClose);

  async function load() {
    try {
      const result = await adminApi.requests();
      setRequests(result.requests);
      onChange(result.requests.length);
      setError(null);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Lecture impossible');
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Refuser supprime la ligne, sans confirmation intermédiaire : la personne
   * peut redemander, et conserver son adresse pour l'empêcher reviendrait à
   * garder précisément ce qu'on vient d'effacer.
   */
  async function act(request: AccountRequest, approve: boolean) {
    setBusy(request.id);
    setError(null);
    try {
      if (approve) await adminApi.approve(request.id);
      else await adminApi.reject(request.id);
      await load();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Opération impossible');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <button type="button" className="overlay" aria-label="Fermer" onClick={onClose} />
      <aside className="panel settings" role="dialog" aria-label="Demandes de compte">
        <div className="panel__grab" aria-hidden="true" />

        <header className="panel__head">
          <div>
            <div className="eyebrow">Administration</div>
            <h2 className="panel__title" style={{ textTransform: 'none' }}>
              Demandes de compte
            </h2>
            <p className="panel__rel">
              {requests == null
                ? 'Chargement…'
                : requests.length === 0
                  ? 'Aucune demande en attente'
                  : `${requests.length} en attente`}
            </p>
          </div>
          <button type="button" className="btn--icon" onClick={onClose} aria-label="Fermer">
            ✕
          </button>
        </header>

        {error ? (
          <p className="error" role="alert" style={{ marginBottom: 12 }}>
            {error}
          </p>
        ) : null}

        {requests?.length === 0 ? (
          <p className="hint">
            Les nouvelles demandes apparaîtront ici, et une notification partira vers vos appareils
            abonnés.
          </p>
        ) : null}

        <div className="requests">
          {requests?.map((request) => (
            <div className="requests__row" key={request.id}>
              <div className="requests__who">
                <b>{request.display_name ?? request.email}</b>
                {request.display_name ? <span className="mono">{request.email}</span> : null}
                <span className="hint">demandé {since(request.created_at)}</span>
              </div>
              <div className="requests__actions">
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={busy !== null}
                  onClick={() => void act(request, true)}
                >
                  Autoriser
                </button>
                <button
                  type="button"
                  className="btn btn--sm btn--ghost"
                  disabled={busy !== null}
                  onClick={() => void act(request, false)}
                >
                  Refuser
                </button>
              </div>
            </div>
          ))}
        </div>

        <p className="weeks__note">
          Refuser supprime la demande et ses données. Rien n’empêche la personne d’en déposer une
          autre — la retenir durablement supposerait de conserver son adresse, c’est-à-dire ce que
          la suppression efface.
        </p>
      </aside>
    </>
  );
}
